import { MapPin, Video, Phone } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Single source of truth for how each meeting type is shown across the app:
 * label, icon, and color. Used on the calendar, agenda, service pickers, the
 * admin services form, and (mirrored) in the transactional emails so a booking
 * format is recognizable at a glance everywhere.
 */
export type MeetingType = 'in_person' | 'video_call' | 'phone_call';

export const MEETING_META: Record<
  MeetingType,
  { label: string; color: string; bg: string; Icon: LucideIcon }
> = {
  in_person: { label: 'In person', color: '#15803d', bg: '#dcfce7', Icon: MapPin },
  video_call: { label: 'Video call', color: '#1d4ed8', bg: '#dbeafe', Icon: Video },
  phone_call: { label: 'Phone call', color: '#6d28d9', bg: '#ede9fe', Icon: Phone },
};

export const MEETING_TYPES: MeetingType[] = ['in_person', 'video_call', 'phone_call'];

/** Coerce a possibly-null/unknown string to a MeetingType, or null. */
export function asMeetingType(v: string | null | undefined): MeetingType | null {
  return v && v in MEETING_META ? (v as MeetingType) : null;
}

/** The offers_* flags a service carries (1/0, as returned by the API). */
export type ServiceModeFlags = {
  offers_in_person?: number | boolean;
  offers_phone?: number | boolean;
  offers_video?: number | boolean;
};

/** The meeting modes a service offers, in canonical display order. */
export function offeredModes(s: ServiceModeFlags): MeetingType[] {
  const out: MeetingType[] = [];
  if (s.offers_in_person) out.push('in_person');
  if (s.offers_video) out.push('video_call');
  if (s.offers_phone) out.push('phone_call');
  return out;
}

/** Colored pill with icon + label. */
export function MeetingTypeBadge({ type, size = 13 }: { type: MeetingType; size?: number }) {
  const m = MEETING_META[type];
  const { Icon } = m;
  return (
    <span className="mt-badge" style={{ background: m.bg, color: m.color }}>
      <Icon size={size} /> {m.label}
    </span>
  );
}

/** Colored icon only (no label). */
export function MeetingTypeIcon({ type, size = 14 }: { type: MeetingType; size?: number }) {
  const m = MEETING_META[type];
  const { Icon } = m;
  return <Icon size={size} color={m.color} aria-label={m.label} />;
}

/** Single-select segmented icon selector. `options` limits which modes are
 * shown (e.g. a service's offered subset in the booking flow); defaults to all. */
export function MeetingTypePicker({
  value,
  onChange,
  options = MEETING_TYPES,
}: {
  value: MeetingType;
  onChange: (t: MeetingType) => void;
  options?: MeetingType[];
}) {
  return (
    <div className="mt-picker">
      {options.map((t) => {
        const m = MEETING_META[t];
        const { Icon } = m;
        const on = value === t;
        return (
          <button
            type="button"
            key={t}
            className={`mt-option${on ? ' mt-option-on' : ''}`}
            style={on ? { borderColor: m.color, background: m.bg, color: m.color } : undefined}
            onClick={() => onChange(t)}
            aria-pressed={on}
          >
            <Icon size={16} /> {m.label}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select mode toggles for the admin services form — a service can offer
 * any non-empty subset of the three modes; the customer picks one at booking. */
export function MeetingModePicker({
  value,
  onChange,
}: {
  value: MeetingType[];
  onChange: (next: MeetingType[]) => void;
}) {
  const toggle = (t: MeetingType) => {
    onChange(value.includes(t) ? value.filter((v) => v !== t) : [...value, t]);
  };
  return (
    <div className="mt-picker">
      {MEETING_TYPES.map((t) => {
        const m = MEETING_META[t];
        const { Icon } = m;
        const on = value.includes(t);
        return (
          <button
            type="button"
            key={t}
            className={`mt-option${on ? ' mt-option-on' : ''}`}
            style={on ? { borderColor: m.color, background: m.bg, color: m.color } : undefined}
            onClick={() => toggle(t)}
            aria-pressed={on}
          >
            <Icon size={16} /> {m.label}
          </button>
        );
      })}
    </div>
  );
}
